import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Facebook, Instagram, Linkedin, Mail, MapPin, Phone, Youtube, Twitter, Send, Globe } from "lucide-react";
import { Brand } from "./Navbar";
import { toast } from "sonner";

const Chakra = () => (
  <svg viewBox="0 0 100 100" className="chakra-spin h-full w-full">
    <circle cx="50" cy="50" r="46" fill="none" stroke="currentColor" strokeWidth="2.5" />
    <circle cx="50" cy="50" r="7" fill="currentColor" />
    {Array.from({ length: 24 }, (_, i) => {
      const a = (i * 15 * Math.PI) / 180;
      return <line key={i} x1={50 + 8 * Math.cos(a)} y1={50 + 8 * Math.sin(a)}
                   x2={50 + 44 * Math.cos(a)} y2={50 + 44 * Math.sin(a)} stroke="currentColor" strokeWidth="1.6" />;
    })}
  </svg>
);

const COLUMNS = [
  { title: "Services", links: [
    { label: "Domestic Courier", to: "/domestic-courier" },
    { label: "International Courier", to: "/international-courier" },
    { label: "eCommerce Shipping", to: "/ecommerce-shipping" },
    { label: "Business Courier", to: "/business-courier" },
    { label: "NRI Courier", to: "/nri-courier" },
  ]},
  { title: "Company", links: [
    { label: "About INDIAFAST", to: "/p/about" },
    { label: "Franchise Program", to: "/partner-with-us" },
    { label: "Become an Agent", to: "/partner-with-us" },
    { label: "Careers", to: "/p/careers" },
    { label: "News & Blog", to: "/p/news" },
    { label: "Media & Videos", to: "/media-videos" },
    { label: "Our Branches", to: "/branches" },
  ]},
  { title: "Support", links: [
    { label: "Track Shipment", to: "/track" },
    { label: "Rate Calculator", to: "/rate-calculator" },
    { label: "Support Tickets", to: "/support" },
    { label: "Raise a Complaint", to: "/complaint" },
    { label: "Pickup Request", to: "/pickup-request" },
    { label: "FAQs", to: "/p/faq" },
    { label: "Raise a Claim", to: "/support?tab=claim" },
  ]},
  { title: "Legal", links: [
    { label: "Terms of Service", to: "/p/terms" },
    { label: "Privacy Policy", to: "/p/privacy" },
    { label: "Refund Policy", to: "/p/refund" },
    { label: "Shipping Policy", to: "/p/shipping" },
    { label: "Prohibited Items", to: "/p/prohibited" },
  ]},
];

const SOCIALS = [
  { icon: Facebook, label: "Facebook", key: "facebook" }, { icon: Instagram, label: "Instagram", key: "instagram" },
  { icon: Twitter, label: "Twitter", key: "twitter" }, { icon: Linkedin, label: "LinkedIn", key: "linkedin" }, { icon: Youtube, label: "YouTube", key: "youtube" },
];

export default function Footer({ contact }) {
  const c = contact || {};
  const [email, setEmail] = useState("");
  const [socials, setSocials] = useState({});

  useEffect(() => {
    api.get("/public/home").then((r) => setSocials((r.data.cms || {}).socials || {})).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    try {
      await api.post("/public/contact", { name: "Newsletter", email, phone: "-", message: "Newsletter subscription" });
      toast.success("Subscribed! INDIAFAST updates will reach your inbox.");
      setEmail("");
    } catch {
      toast.error("Could not subscribe right now. Please try again.");
    }
  };

  return (
    <footer data-testid="site-footer" className="relative overflow-hidden bg-[#F4F3F1] text-slate-600">
      <div className="tricolor-beam h-1.5 w-full" />

      <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 text-navy/[0.05]"><Chakra /></div>
      <div className="pointer-events-none absolute -bottom-32 -left-32 h-[28rem] w-[28rem] text-navy/[0.04]"><Chakra /></div>

      <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <div className="grid items-center gap-8 border-b border-stone-300 pb-12 lg:grid-cols-2">
          <div>
            <Brand testid="footer-brand-logo" />
            <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-500">
              Ship Faster. Track Smarter. Deliver Worldwide. India's technology-first courier network —
              29,000+ pincodes at home, 220+ countries across the globe.
            </p>
            <div className="mt-5 flex items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full border border-stone-300 bg-white px-4 py-1.5 text-xs font-bold text-navy">
                <span className="flex h-3.5 w-6 overflow-hidden rounded-sm">
                  <span className="h-full w-1/3 bg-[#FF9933]" /><span className="h-full w-1/3 bg-white" /><span className="h-full w-1/3 bg-[#138808]" />
                </span>
                Proudly Made in India
              </span>
            </div>
          </div>

          <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
            <h3 className="font-heading text-lg font-semibold text-navy">Get shipping offers & logistics insights</h3>
            <p className="mt-1 text-xs text-slate-500">Festive rate drops, new lanes and product updates — once a month, no spam.</p>
            <form onSubmit={subscribe} className="mt-4 flex gap-2">
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                     data-testid="footer-newsletter-input" placeholder="your@email.com"
                     className="w-full rounded-xl border border-stone-300 bg-stone-50 px-4 py-2.5 text-sm text-navy placeholder-slate-400 outline-none focus:border-saffron" />
              <button type="submit" data-testid="footer-subscribe-btn"
                      className="flex shrink-0 items-center gap-2 rounded-xl bg-saffron px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-saffron-hover">
                <Send className="h-4 w-4" /> Subscribe
              </button>
            </form>
          </div>
        </div>

        <div className="grid gap-10 py-12 sm:grid-cols-2 lg:grid-cols-6">
          <div className="lg:col-span-2">
            <h4 className="font-heading text-sm font-bold uppercase tracking-[0.15em] text-navy">Get in touch</h4>
            <div className="mt-4 space-y-3 text-sm">
              <p className="flex items-center gap-2.5"><Phone className="h-4 w-4 shrink-0 text-saffron" /> {c.phone || "+91 97838 07838"}</p>
              <p className="flex items-center gap-2.5"><Mail className="h-4 w-4 shrink-0 text-saffron" /> {c.email || "ccare@indiafast.in"}</p>
              <p className="flex items-center gap-2.5"><Globe className="h-4 w-4 shrink-0 text-saffron" /> {c.website || "www.indiafast.in"}</p>
              <p className="flex items-start gap-2.5"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-saffron" />
                {c.address || "Plot no 36, Nagi Reddy Colony Rd, Raja Rajeswari Nagar, Old Bowenpally, Hyderabad, Secunderabad, Telangana 500011"}</p>
            </div>
            <div className="mt-6 flex gap-2.5" data-testid="footer-socials">
              {SOCIALS.filter((s) => socials[s.key]).map((s) => (
                <a key={s.label} href={socials[s.key]} target="_blank" rel="noreferrer" aria-label={s.label} data-testid={`footer-social-${s.key}`}
                   className="grid h-9 w-9 place-items-center rounded-full border border-stone-300 bg-white text-slate-500 transition-all hover:border-saffron hover:bg-saffron hover:text-white">
                  <s.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
          {COLUMNS.map((col) => (
            <div key={col.title}>
              <h4 className="font-heading text-sm font-bold uppercase tracking-[0.15em] text-navy">{col.title}</h4>
              <ul className="mt-4 space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          className="group flex items-center gap-1.5 text-sm text-slate-500 transition-colors hover:text-saffron">
                      <span className="h-px w-0 bg-saffron transition-all group-hover:w-3" />{l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="flex flex-col items-center justify-between gap-4 border-t border-stone-300 pt-8 text-xs text-slate-500 sm:flex-row">
          <p>© 2026 INDIAFAST Global Couriers Pvt. Ltd. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <span>Gurugram · Mumbai · Bengaluru · Hyderabad</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
